import startCase from "lodash/startCase";
import uniq from "lodash/uniq";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import { camelizer } from "@stadium/helpers/src/formatter";

import { ON_DEMAND_ITEM } from "@stadium/checkout/src/constants/order";

export const checkFetchRewardPointsRequired = (response) => {
  const result = camelizer(response);
  return result?.data?.attributes?.useGlobalPoint;
};

const getProductSetRestrictions = (order, productSet) => {
  const { spreeOrderProductSetRestrictions = {} } = order;
  const { orderProductSetRestrictions = [] } = productSet;
  const restrictions = orderProductSetRestrictions.reduce((acc, { id }) => {
    const restrictionIds = spreeOrderProductSetRestrictions[id]?.taxonIds || [];
    acc = [...acc, ...restrictionIds];
    return acc;
  }, []);
  return uniq(restrictions);
};

const getProductSetDietaryRestriction = (order, productSet) => {
  const { spreeOrderProductSetRestrictions = {} } = order;
  const { orderProductSetRestrictions = [] } = productSet;
  let preference = "";
  let isRestrictionsSelected = false;

  const restrictions = orderProductSetRestrictions.reduce((acc, { id }) => {
    const productSetRestriction = spreeOrderProductSetRestrictions[id] || {};
    const { dietaryRestrictions, dietaryPreference } = productSetRestriction;

    const restrictionIds = dietaryRestrictions || {};
    acc = [...acc, ...Object.keys(restrictionIds)];
    preference = dietaryPreference || "";
    isRestrictionsSelected = productSetRestriction?.isRestrictionsSelected;
    return acc;
  }, []);
  return { restrictions: uniq(restrictions), preference, isRestrictionsSelected };
};

const getAuthorizedPaymentsData = (spreePayments) =>
  Object.values(spreePayments).reduce((acc, item) => {
    if (acc[item.paymentType])
      acc[item.paymentType].originalAmount =
        +acc[item.paymentType].originalAmount + +item.originalAmount;
    else acc[item.paymentType] = item;
    return acc;
  }, {});

export const formatOrder = (order = {}) => {
  const {
    spreeLeadOrders = {},
    spreeOrganizerOrders = {},
    spreeRecipientOrders = {},
    spreeWalletTopUpOrders = {},
    spreeGiftDetails = {},
    spreeOrganizerShares = {},
    spreeUsers = {},
    spreePayments = {},
    spreeCreditCards = {},
    spreeBillingDetails = {},
    spreeRecipientZones = {},
    spreeLineItems = {},
    spreeOrderProductSets = {},
    spreeMedleyItemSets = {},
    spreeVideoMessages = {},
    spreeMessages = {},
    spreeLineItemCustomizations = {},
  } = order;

  const organizerOrder = Object.values(spreeOrganizerOrders)[0];

  const baseOrder =
    organizerOrder ||
    Object.values(spreeLeadOrders)[0] ||
    Object.values(spreeRecipientOrders)[0] ||
    Object.values(spreeWalletTopUpOrders)[0] ||
    {};

  const groupedLineItemsByMedley = groupBy(spreeLineItems, "medleyItemSetId");
  const lineItems = groupedLineItemsByMedley["null"] || [];
  if (!isEmpty(lineItems)) {
    lineItems.forEach((lineItem) => {
      const lineItemCustomizationId = lineItem?.lineItemCustomization?.id;
      const customization = spreeLineItemCustomizations[lineItemCustomizationId];
      lineItem.lineItemCustomization = customization;
    });
  }
  const productSets = Object.values(spreeOrderProductSets) || [];
  const medleyItemSets = Object.values(spreeMedleyItemSets) || [];
  delete groupedLineItemsByMedley["null"];

  const itemCount = [lineItems, productSets, medleyItemSets].reduce(
    (total, items) => total + items.reduce((qty, item) => qty + item.quantity, 0),
    0,
  );

  const cartProductZoneList = uniq([
    ...lineItems.map((item) => item.zoneId),
    ...productSets.map((item) => item.zoneId),
    ...medleyItemSets.map((item) => item.zoneId),
  ]).filter(Boolean);

  const getSum = (total, { quantity, price }) => total + quantity * price;
  medleyItemSets.forEach((item) => {
    const components = groupedLineItemsByMedley[item.id];
    item.medleyComponents = components;
    item.price = components?.reduce(getSum, 0);
    item.trackInventory = false;
    item.itemType = ON_DEMAND_ITEM;
  });

  productSets.forEach((item) => {
    item.trackInventory = false;
    item.itemType = ON_DEMAND_ITEM;
    item.restrictions = getProductSetRestrictions?.(order, item);
    const { restrictions, preference, isRestrictionsSelected } =
      getProductSetDietaryRestriction?.(order, item) || {};
    item.dietaryRestriction = restrictions;
    item.dietaryPreference = preference;
    item.isRestrictionsSelected = isRestrictionsSelected;
  });

  const allProducts = [...lineItems, ...productSets, ...medleyItemSets];
  return {
    ...baseOrder,
    ownerDetails: organizerOrder?.ownerDetails || baseOrder.ownerDetails,
    user: Object.values(spreeUsers)[0] || {},
    videoMessage: Object.values(spreeVideoMessages)[0] || {},
    giftDetail: Object.values(spreeGiftDetails)[0] || {},
    billingDetail: Object.values(spreeBillingDetails)[0] || {},
    organizerShare: Object.values(spreeOrganizerShares)[0] || null,
    recipientZones: Object.values(spreeRecipientZones),
    senderRlpMessage: Object.values(spreeMessages)[0] || {},

    lineItems,
    productSets,
    medleyItemSets,
    allProductList: allProducts,

    itemCount,
    cartProductZoneList,

    authorizedPayments: getAuthorizedPaymentsData(spreePayments),
    payments: (baseOrder?.payments || []).map(({ id }) => spreePayments?.[id]),
    cardDetails: spreeCreditCards[baseOrder?.creditCard?.id] || {},
    creditCardId: baseOrder?.creditCard?.id || baseOrder?.creditCardId,

    isCompleted: Boolean(baseOrder.completedAt),
    isExpired: Boolean(baseOrder.expiredOn),
    isCustomizationWarehouseItemAvailable: allProducts.some(
      ({ shippedSeparately }) => !shippedSeparately,
    ),
    isCustomizationOnDemandItemAvailable: allProducts.some(({ trackInventory }) => !trackInventory),
  };
};

export const formatOrderUpdateErrors = (error) => {
  const errorArr =
    Array.isArray(error?.errors) && error.errors?.[0]?.detail
      ? error.errors
      : error?.meta?.errors || [];
  let errors = errorArr.map(({ detail, source }) => {
    const keyArr = source?.pointer?.split("/");
    const key = keyArr?.[keyArr.length - 1]?.split(".") ?? [];
    return `${startCase(key)} ${detail || ""}`.trim();
  });
  return errors;
};

export const formatOrderCheckoutErrors = (error) => {
  const errorArr = error?.data?.attributes?.error_details?.errors || [];
  const errorResponse = { meta: { errors: errorArr } };
  return formatOrderUpdateErrors(errorResponse);
};
